import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, MapPin, ClipboardList, Share2, Star, Check, X, Share, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { BDAFDots, tierFor } from './BDAFDots';

export const SIGNAL_ICONS = { website: Globe, google_business: MapPin, directory_listing: ClipboardList, social_media: Share2, reviews: Star };

export const SIGNAL_HINTS = {
  website: { ok: 'Found and active', gap: 'No website detected — customers cannot find you at all', cta: 'Add website' },
  google_business: { ok: 'Verified listing found', gap: 'Not found in Google search — you are invisible in local search', cta: 'Set up free' },
  directory_listing: { ok: 'Listed on business directories', gap: 'Not listed on Yell, Thomson, or any sector directory', cta: 'Add listings' },
  social_media: { ok: 'Active social profiles found', gap: 'No active social media profiles found', cta: 'Link profiles' },
  reviews: { ok: 'Customer reviews found', gap: 'No reviews found on Google, Trustpilot, or Checkatrade', cta: 'Get reviews' },
};

export function SignalRow({ signal, action, compact = false }) {
  const Icon = SIGNAL_ICONS[signal.key] || Globe;
  const hint = SIGNAL_HINTS[signal.key] || {};
  return (
    <div className={`flex items-center gap-3 ${compact ? 'py-2' : 'py-3'} border-b border-warm-border last:border-0`} data-testid={`signal-row-${signal.key}`}>
      <span className={`w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 ${signal.detected ? 'bg-forest-light text-forest' : 'bg-bg-warm text-mid'}`}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-body text-sm font-semibold text-ink">{signal.label}</p>
        <p className={`font-body text-xs flex items-center gap-1 ${signal.status_text ? 'text-status-amber' : signal.detected ? 'text-forest' : 'text-mid'}`}>
          {signal.detected ? <Check className="w-3 h-3" strokeWidth={2.5} /> : <X className="w-3 h-3" strokeWidth={2.5} />}
          {signal.status_text || (signal.detected ? hint.ok : hint.gap)}
        </p>
        <div className="mt-1.5 h-1 rounded-full bg-bg-warm overflow-hidden w-32">
          <div className="h-full rounded-full transition-all" style={{ width: signal.detected ? '100%' : '0%', background: '#1A4D3A' }} />
        </div>
      </div>
      {action && !signal.detected && action(signal, hint)}
    </div>
  );
}

// Self-audit widget: standalone (/audit/:id) + embedded in owner dashboard
export function BDAFAuditWidget({ data, ownerMode = false }) {
  const [copied, setCopied] = useState(false);
  if (!data) return null;
  const score = data.bdaf_score ?? 0;
  const t = tierFor(score);
  const gaps = (data.signals || []).filter(s => !s.detected).length;
  const share = async () => {
    const url = `${window.location.origin}/audit/${data.id}`;
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success('Audit link copied'); setTimeout(() => setCopied(false), 1500); } catch { toast.error('Could not copy link'); }
  };
  return (
    <div className="solid-card overflow-hidden" data-testid="bdaf-audit-widget">
      <div className="p-5 border-b border-warm-border flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="font-body text-[11px] uppercase tracking-[0.12em] text-mid font-semibold">BDAF Self-Audit</p>
          <h3 className="font-display text-lg font-bold text-ink truncate">{data.company_name}</h3>
          <p className="font-body text-xs text-mid">{data.postcode_district} · {data.sector}{data.established ? ` · Est. ${String(data.established).slice(-4)}` : ''}</p>
        </div>
        <BDAFDots score={score} size={10} showLabel />
      </div>
      <div className="px-5">
        {(data.signals || []).map(s => <SignalRow key={s.key} signal={s} compact />)}
      </div>
      <div className="p-5 bg-bg-warm/60 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="font-body text-xs text-mid">Your BDAF score</p>
          <p className="font-display text-3xl font-extrabold" style={{ color: t.color }} data-testid="audit-score">{score} <span className="text-base text-mid font-semibold">/ 5</span></p>
          <p className="font-body text-xs text-ink-2">Tier: <b>{t.label}</b>{gaps > 0 ? ` · ${gaps} signal${gaps > 1 ? 's' : ''} to close` : ' · All signals detected'}</p>
        </div>
        <div className="flex items-center gap-2">
          {!ownerMode && (
            <Link to={`/claim/${data.id}`} data-testid="audit-fix-gaps" className="inline-flex items-center px-4 py-2 rounded-full bg-brand-orange text-white font-body text-sm font-semibold hover:bg-highlight-orange transition-colors">
              {gaps > 0 ? `Fix my ${gaps} gap${gaps > 1 ? 's' : ''} →` : 'Claim this profile →'}
            </Link>
          )}
          <button onClick={share} data-testid="audit-share" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-warm-border bg-white text-ink font-body text-sm font-medium hover:bg-bg-warm transition-colors">
            {copied ? <Copy className="w-3.5 h-3.5" /> : <Share className="w-3.5 h-3.5" />} Share score
          </button>
        </div>
      </div>
    </div>
  );
}
